import { useState, useEffect } from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { Clock, Calendar, ArrowLeft } from 'lucide-react';
import SiteNav from '../components/SiteNav';
import { contentApi } from '../lib/api';

export default function BlogPostPage() {
  const { slug } = useParams();
  const [post, setPost] = useState(undefined); // undefined = loading, null = not found

  useEffect(() => {
    contentApi.getBlogPost(slug)
      .then(r => setPost(r.data))
      .catch(() => setPost(null));
  }, [slug]);

  if (post === null) return <Navigate to="/blog" replace />;

  return (
    <>
      {post && (
        <>
          <title>{post.title} | The Kalari Movement</title>
          <meta name="description" content={post.excerpt || ''} />
          <meta property="og:title" content={`${post.title} | The Kalari Movement`} />
          <meta property="og:description" content={post.excerpt || ''} />
          <meta property="og:url" content={`https://thekalarimovement.com/blog/${post.slug}`} />
          <meta property="og:type" content="article" />
          {post.hero_image_url && <meta property="og:image" content={post.hero_image_url} />}
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content={`${post.title} | The Kalari Movement`} />
          <meta name="twitter:description" content={post.excerpt || ''} />
          {post.hero_image_url && <meta name="twitter:image" content={post.hero_image_url} />}
          <link rel="canonical" href={`https://thekalarimovement.com/blog/${post.slug}`} />
        </>
      )}
      <div className="min-h-screen bg-[#FDFBF7]">
        <SiteNav />
        <div className="pt-16">
          {post === undefined ? <PostSkeleton /> : <PostContent post={post} />}
        </div>
      </div>
    </>
  );
}

function PostSkeleton() {
  return (
    <div className="max-w-3xl mx-auto px-6 py-20 space-y-4 animate-pulse">
      <div className="h-4 bg-[#E7E5E4] rounded w-24 mb-8" />
      <div className="h-10 bg-[#E7E5E4] rounded w-3/4" />
      <div className="h-4 bg-[#E7E5E4] rounded w-1/2" />
      <div className="h-72 bg-[#E7E5E4] rounded-2xl mt-8" />
    </div>
  );
}

function PostContent({ post }) {
  return (
    <>
      <div className="bg-[#1C1917] py-16 px-6">
        <div className="max-w-3xl mx-auto">
          <Link
            to="/blog"
            className="inline-flex items-center gap-2 text-[#DDA15E] text-sm font-medium mb-8 hover:text-white transition-colors"
          >
            <ArrowLeft className="h-4 w-4" /> Back to Blog
          </Link>

          {post.category && (
            <p className="text-[#DDA15E] text-xs font-semibold uppercase tracking-widest mb-4">{post.category}</p>
          )}

          <h1 className="font-heading text-3xl md:text-5xl font-bold text-white mb-6 leading-tight">
            {post.title}
          </h1>

          <div className="flex flex-wrap items-center gap-5 text-white/50 text-sm">
            <span className="font-medium text-white/70">{post.author || 'The Kalari Movement'}</span>
            {post.created_at && (
              <span className="flex items-center gap-1.5">
                <Calendar className="h-3.5 w-3.5" />
                {new Date(post.created_at).toLocaleDateString('en-GB', {
                  day: 'numeric', month: 'long', year: 'numeric',
                })}
              </span>
            )}
            {post.read_time_mins && (
              <span className="flex items-center gap-1.5">
                <Clock className="h-3.5 w-3.5" />
                {post.read_time_mins} min read
              </span>
            )}
          </div>
        </div>
      </div>

      {post.hero_image_url && (
        <div className="max-w-3xl mx-auto px-6 -mt-8">
          <div className="rounded-2xl overflow-hidden shadow-2xl">
            <img src={post.hero_image_url} alt={post.title} className="w-full object-cover" />
          </div>
        </div>
      )}

      <article className="max-w-3xl mx-auto px-6 py-16">
        {post.excerpt && (
          <p className="text-xl text-[#78716C] leading-relaxed mb-10 font-medium italic border-l-4 border-[#DDA15E] pl-6">
            {post.excerpt}
          </p>
        )}
        <div
          className="prose-kalari"
          dangerouslySetInnerHTML={{ __html: post.content }}
        />

        <div className="mt-16 pt-8 border-t border-[#E7E5E4] flex items-center justify-between flex-wrap gap-4">
          {post.tags && post.tags.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {post.tags.map(tag => (
                <span key={tag} className="text-xs text-[#78716C] bg-[#F4EFE6] px-3 py-1 rounded-full">
                  {tag}
                </span>
              ))}
            </div>
          )}
          <Link to="/blog" className="text-sm font-semibold text-[#9A3B26] hover:text-[#7A2E1E] flex items-center gap-1">
            <ArrowLeft className="h-4 w-4" /> More writings
          </Link>
        </div>
      </article>

      <footer className="bg-[#141210] text-white/50 py-8 px-6 text-center text-sm">
        © {new Date().getFullYear()} The Kalari Movement ·{' '}
        <a href="mailto:connect@thekalarimovement.com" className="hover:text-white transition-colors">
          connect@thekalarimovement.com
        </a>
      </footer>
    </>
  );
}
